import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import '../styles/InventoryVideoModal.css';

/**
 * InventoryVideoModal
 * --------------------------------------------------------------------------
 * Auto-opens when the Inventory page loads and plays a video. Darkens + blurs
 * the page behind it. Mobile + desktop friendly. Shows once per browser
 * session so it isn't annoying on every visit.
 *
 * 👉 Swap VIDEO_SRC for the real clip when ready.
 */

const VIDEO_SRC = '/assets/videos/intro-video.mp4'; // TODO: replace with real video
const SESSION_KEY = 'inventory_video_seen';

export default function InventoryVideoModal() {
  const [open, setOpen] = useState(false);
  const videoRef = useRef(null);

  /* ---- open on mount (once per session) ---- */
  useEffect(() => {
    let seen = false;
    try {
      seen = sessionStorage.getItem(SESSION_KEY) === '1';
    } catch {
      /* sessionStorage may be unavailable (private mode) — just show it */
    }
    if (!seen) setOpen(true);
  }, []);

  /* ---- lock scroll + close on Escape while open ---- */
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    setOpen(false);
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="ivm-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label="Featured video"
        >
          <motion.div
            className="ivm-frame"
            initial={{ opacity: 0, scale: 0.94, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 14 }}
            transition={{ type: 'spring', stiffness: 280, damping: 28, mass: 0.9 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="ivm-close"
              aria-label="Close"
              onClick={close}
            >
              <X size={20} />
            </button>

            <video
              ref={videoRef}
              className="ivm-video"
              src={VIDEO_SRC}
              autoPlay
              loop
              playsInline
              controls
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
